'use client';

import { SignUp, useUser } from '@clerk/nextjs';
import React from 'react';
import { AuthProgressBar, SuccessPanel } from '@/legacy-spa/components/guest-ui/primitives';
import { UniversalSignInFlow } from './UniversalSignInFlow';

const OPEN_EVENT = 'dm:universal-sign-in:open';
const RETURN_KEY = 'dm-auth-return';

let pendingOpen = false;

// A completion that navigates unmounts the host, so the landing strip must be marker-based.
const LANDING_KEY = 'dm-auth-landed-at';

export const markAuthLanding = () => {
  try {
    sessionStorage.setItem(LANDING_KEY, String(Date.now()));
  } catch {}
};

const consumeAuthLanding = (): boolean => {
  try {
    const raw = sessionStorage.getItem(LANDING_KEY);
    if (!raw) return false;
    sessionStorage.removeItem(LANDING_KEY);
    // Stale markers (e.g. an abandoned OAuth attempt) don't greet later visits.
    return Date.now() - Number(raw) < 2 * 60 * 1000;
  } catch {
    return false;
  }
};

export const openUniversalSignIn = (opts?: { returnTo?: string }) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(RETURN_KEY, opts?.returnTo ?? window.location.href);
  } catch {
    // Storage unavailable (private mode) — the fallback is the current URL.
  }
  pendingOpen = true;
  window.dispatchEvent(new Event(OPEN_EVENT));
};

const safeReturnUrl = (): string => {
  try {
    const raw = sessionStorage.getItem(RETURN_KEY);
    if (raw) {
      const url = new URL(raw, window.location.origin);
      if (url.origin === window.location.origin) return url.toString();
    }
  } catch {}
  return window.location.href;
};

const destinationLabel = (dest: string): string => {
  try {
    const path = new URL(dest, window.location.origin).pathname;
    if (path.startsWith('/trips')) return 'My Trips';
    if (path.startsWith('/passport')) return 'your Passport';
    if (path.startsWith('/confirmation')) return 'your reservation';
    if (path === '/' || path === '') return 'the home page';
  } catch {}
  return 'where you left off';
};

// The shell is the card; strip the prebuilt's own chrome so they don't nest.
const EMBEDDED_APPEARANCE = {
  elements: {
    rootBox: { width: '100%' },
    cardBox: { width: '100%', boxShadow: 'none' },
    card: { border: 'none', boxShadow: 'none', padding: '4px 0 0', width: '100%' },
    footer: { display: 'none' },
    logoBox: { display: 'none' },
  },
} as const;

export const UniversalSignInHost = () => {
  const { isSignedIn, isLoaded } = useUser();
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState<'signIn' | 'signUp'>('signIn');
  const [phase, setPhase] = React.useState<'auth' | 'success'>('auth');
  const [returnUrl, setReturnUrl] = React.useState<string | null>(null);
  const [landed, setLanded] = React.useState(false);

  React.useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (consumeAuthLanding()) {
      setLanded(true);
      const t = setTimeout(() => setLanded(false), 6000);
      return () => clearTimeout(t);
    }
  }, [isLoaded, isSignedIn]);

  const dest = returnUrl ?? (typeof window !== 'undefined' ? window.location.href : '');

  const finish = React.useCallback(() => {
    setOpen(false);
    setPhase('auth');
    if (typeof window === 'undefined') return;
    const target = returnUrl ?? window.location.href;
    if (target !== window.location.href) {
      markAuthLanding();
      window.location.assign(target);
    }
  }, [returnUrl]);

  React.useEffect(() => {
    const onOpen = () => {
      pendingOpen = false;
      setMode('signIn');
      setPhase('auth');
      setReturnUrl(safeReturnUrl());
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    if (pendingOpen) onOpen();
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open || !isSignedIn || phase === 'success') return;
    setPhase('success');
  }, [open, isSignedIn, phase]);

  React.useEffect(() => {
    if (!open || phase !== 'success') return;
    const t = setTimeout(finish, 1600);
    return () => clearTimeout(t);
  }, [open, phase, finish]);

  if (!open) {
    if (!landed) return null;
    return (
      <div className="dm-usi-landed" role="status">
        <style>{`
          .dm-usi-landed {
            position: fixed;
            top: 12px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 1600;
            max-width: min(560px, calc(100vw - 32px));
            display: flex;
            align-items: center;
            gap: 12px;
            background: #c6f6d5;
            color: #22543d;
            border-radius: 6px;
            padding: 12px 14px;
            font: 500 13px/19px 'azo-sans-web', sans-serif;
            box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
          }
          .dm-usi-landed button {
            border: none;
            background: none;
            color: #22543d;
            font-size: 14px;
            cursor: pointer;
            padding: 0 2px;
          }
        `}</style>
        <span>Signed in — welcome back.</span>
        <button type="button" aria-label="Dismiss" onClick={() => setLanded(false)}>
          ✕
        </button>
      </div>
    );
  }

  return (
    <div
      className="dm-usi-scrim"
      onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}
    >
      <style>{`
        .dm-usi-scrim {
          position: fixed;
          inset: 0;
          z-index: 1600;
          background: rgba(15, 34, 56, 0.5);
          overflow-y: auto;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding: 84px 16px 40px;
          font-family: 'azo-sans-web', sans-serif;
        }
        .dm-usi-card {
          position: relative;
          width: 440px;
          max-width: 100%;
          background: #ffffff;
          border-radius: 12px;
          padding: 28px;
          box-shadow: 0 24px 48px rgba(15, 34, 56, 0.18);
        }
        .dm-usi-header { display: none; }
        .dm-usi-close {
          position: absolute;
          top: 16px;
          right: 18px;
          z-index: 1;
          border: none;
          background: none;
          font-size: 18px;
          line-height: 1;
          color: #718096;
          cursor: pointer;
          padding: 4px;
        }
        .dm-usi-footer {
          margin-top: 18px;
          padding-top: 14px;
          border-top: 1px solid #e2e8f0;
          font: 400 12px/17px 'azo-sans-web', sans-serif;
          color: #6a6a6a;
          text-align: center;
        }
        .dm-usi-footer a {
          color: #0f2238;
          font-weight: 500;
          text-decoration: underline;
          text-underline-offset: 3px;
          cursor: pointer;
        }
        .dm-usi-note {
          margin: 0 0 14px;
          background: #f7fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 12px 14px;
          font: 400 13px/18px 'azo-sans-web', sans-serif;
          color: #4a5568;
          text-align: left;
        }
        .dm-usi-success {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 24px 0 8px;
          text-align: center;
        }
        .dm-usi-gonow {
          font: 500 13px/18px 'azo-sans-web', sans-serif;
          color: #0f2238;
          text-decoration: underline;
          text-underline-offset: 3px;
          cursor: pointer;
          border: none;
          background: none;
        }
        @media (max-width: 767px) {
          .dm-usi-scrim {
            background: #ffffff;
            padding: 0;
            align-items: stretch;
          }
          .dm-usi-card {
            width: 100%;
            min-height: 100vh;
            border-radius: 0;
            box-shadow: none;
            padding: 0 20px 32px;
          }
          .dm-usi-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            height: 56px;
            margin: 0 -20px 18px;
            padding: 0 16px;
            border-bottom: 1px solid #e2e8f0;
          }
          .dm-usi-header img { height: 20px; }
          .dm-usi-header .dm-usi-spacer { width: 28px; }
          .dm-usi-close { position: static; padding: 4px 8px; color: #0f2238; font-size: 20px; }
          .dm-usi-close.dm-usi-close-desktop { display: none; }
        }
        @media (min-width: 768px) {
          .dm-usi-close.dm-usi-close-mobile { display: none; }
        }
      `}</style>
      <div className="dm-usi-card" role="dialog" aria-modal="true" aria-label="Sign in to Del Mar">
        <div className="dm-usi-header">
          <button
            type="button"
            className="dm-usi-close dm-usi-close-mobile"
            aria-label="Close sign in"
            onClick={() => setOpen(false)}
          >
            ✕
          </button>
          <img src="/logo.svg" alt="Del Mar Vacations" />
          <span className="dm-usi-spacer" />
        </div>
        <button
          type="button"
          className="dm-usi-close dm-usi-close-desktop"
          aria-label="Close sign in"
          onClick={() => setOpen(false)}
        >
          ✕
        </button>

        {phase === 'success' ? (
          <div className="dm-usi-success">
            <SuccessPanel
              heading="You're signed in"
              body={`Taking you back to ${destinationLabel(dest)}…`}
              actions={<AuthProgressBar value={72} />}
              footer={
                <button type="button" className="dm-usi-gonow" onClick={finish}>
                  Go now
                </button>
              }
            />
          </div>
        ) : mode === 'signIn' ? (
          <>
            <UniversalSignInFlow
              returnUrl={dest}
              onFindReservation={() => setMode('signUp')}
              onAlreadySignedIn={() => setPhase('success')}
            />
            <div className="dm-usi-footer">
              Booked with Del Mar but never set up an account?{' '}
              <a onClick={() => setMode('signUp')}>Find my reservation</a>
            </div>
          </>
        ) : (
          <>
            <p className="dm-usi-note">
              Create your free account first — then we&apos;ll find your trip with your confirmation
              number and check-in date from My Trips.
            </p>
            <SignUp
              routing="virtual"
              forceRedirectUrl={dest}
              signInForceRedirectUrl={dest}
              appearance={EMBEDDED_APPEARANCE}
            />
            <div className="dm-usi-footer">
              Already have an account? <a onClick={() => setMode('signIn')}>Sign in</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
